<template>
    <Teleport to="body">
        <Transition name="modal-fade">
            <div
                v-if="isVisible"
                class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-[2px]"
                @click.self="closeModal"
            >
                <div
                    class="w-full max-w-lg md:max-w-2xl max-h-[90vh] bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden flex flex-col"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="integration-details-title"
                >
                    <div class="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
                        <div class="flex-1 pr-4">
                            <h2 id="integration-details-title" class="text-lg font-semibold text-gray-900">
                                Подробнее об интеграциях
                            </h2>
                            <p class="text-sm text-gray-500 mt-1">
                                Статистика использования и состояние подключённых сервисов
                            </p>
                        </div>

                        <button
                            type="button"
                            class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            aria-label="Закрыть"
                            @click="closeModal"
                        >
                            <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    <div class="px-6 py-5 overflow-y-auto space-y-4">
                        <div
                            v-for="integration in integrations"
                            :key="integration.id"
                            class="rounded-xl border border-gray-100 bg-gray-50/50 p-5"
                        >
                            <div class="flex items-start justify-between gap-4 mb-4">
                                <div class="flex items-center gap-3 min-w-0">
                                    <div class="w-10 h-10 bg-white border border-gray-100 rounded-lg flex items-center justify-center shrink-0">
                                        <component :is="integration.icon" class="size-6" />
                                    </div>
                                    <div class="min-w-0">
                                        <h3 class="text-base font-semibold text-gray-900 truncate">
                                            {{ integration.name }}
                                        </h3>
                                        <p class="text-xs text-gray-500 mt-0.5">
                                            {{ integration.type }}
                                        </p>
                                    </div>
                                </div>
                                <StatusBadge :status="integration.status" />
                            </div>

                            <p class="text-sm text-gray-600 mb-4">
                                {{ integration.description }}
                            </p>

                            <div class="grid grid-cols-2 gap-3">
                                <div class="rounded-lg bg-white border border-gray-100 px-3 py-2.5">
                                    <p class="text-xs text-gray-500 mb-1">Соединений</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ integration.connectionsCount }}</p>
                                </div>
                                <div class="rounded-lg bg-white border border-gray-100 px-3 py-2.5">
                                    <p class="text-xs text-gray-500 mb-1">Последняя синхронизация</p>
                                    <p class="text-sm font-medium text-gray-900">{{ integration.lastSync }}</p>
                                </div>
                                <div class="rounded-lg bg-white border border-gray-100 px-3 py-2.5">
                                    <p class="text-xs text-gray-500 mb-1">Аккаунт</p>
                                    <p class="text-sm font-medium text-gray-900 truncate" :title="integration.account">
                                        {{ integration.account }}
                                    </p>
                                </div>
                                <div class="rounded-lg bg-white border border-gray-100 px-3 py-2.5">
                                    <p class="text-xs text-gray-500 mb-1">Подключена</p>
                                    <p class="text-sm font-medium text-gray-900">{{ integration.connectedAt }}</p>
                                </div>
                            </div>

                            <div
                                v-if="integration.status !== 'Подключено'"
                                class="mt-4 rounded-lg px-3 py-2.5 text-xs"
                                :class="integration.status === 'Ошибка в подключении'
                                    ? 'bg-red-50 text-red-700 border border-red-100'
                                    : 'bg-amber-50 text-amber-700 border border-amber-100'"
                            >
                                {{ integration.statusHint }}
                            </div>

                            <div
                                v-if="isGoogleIntegration(integration.code)"
                                class="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2"
                            >
                                <button
                                    type="button"
                                    class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                    @click="reconnectGoogle"
                                >
                                    Переподключить
                                </button>
                                <button
                                    v-if="integration.isConnected"
                                    type="button"
                                    class="px-3 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                                    :disabled="isDisconnecting"
                                    @click="disconnectGoogle"
                                >
                                    {{ isDisconnecting ? 'Отключение...' : 'Отключить' }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center justify-end px-6 py-4 bg-gray-50 border-t border-gray-100 shrink-0">
                        <button
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            @click="closeModal"
                        >
                            Закрыть
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { defineEmits, defineModel, defineProps, ref } from 'vue';
import { createToaster } from "@meforma/vue-toaster";
import StatusBadge from "@/pages/UserProfile/components/StatusBadge.vue";
import { isGoogleIntegration } from "@/pages/UserProfile/utils/integrationMapper";
import { Api } from "@/service/api";

const toaster = createToaster();

defineProps({
    integrations: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['refresh-integrations']);

const isVisible = defineModel();
const isDisconnecting = ref(false);

function closeModal() {
    isVisible.value = false;
}

async function reconnectGoogle() {
    const url = await Api.getGoogleOauthUrl();
    window.after_authorization = async () => {
        emit('refresh-integrations');
    };
    window.open(url, '_blank', 'top=100, left=100, width=400, height=500');
}

async function disconnectGoogle() {
    isDisconnecting.value = true;

    try {
        await Api.GoogleLogOut();
        toaster.success('Google аккаунт отключён');
        emit('refresh-integrations');
    } catch (error) {
        console.error('Failed to disconnect Google account', error);
        toaster.error('Не удалось отключить Google аккаунт');
    } finally {
        isDisconnecting.value = false;
    }
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}
</style>
